// Who's signed in. /session/config comes from this app's Worker (brand id,
// Turnstile key, whether a session cookie exists); /api/v1/me from the API.
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, UNAUTHORISED_EVENT, ok } from '@cover/core/api/client.ts';
import type { Me } from '@cover/core/api/types.ts';
import { api, keys } from './api.ts';

export type SessionConfig = { brand: string; turnstileSiteKey: string; signedIn: boolean; demoNotice: boolean };

export function useSessionConfig() {
  return useQuery({
    queryKey: ['session-config'],
    queryFn: async () => (await fetch('/session/config', { credentials: 'same-origin' })).json() as Promise<SessionConfig>,
    staleTime: Infinity,
  });
}

type Session = { me: Me; businessId: string; setBusinessId: (id: string) => void };
const SessionContext = createContext<Session | null>(null);

export function useMe() {
  return useQuery({
    queryKey: keys.me,
    queryFn: () => ok(api.GET('/me')),
    retry: (n, e) => !(e instanceof ApiError && e.status === 401) && n < 2,
  });
}

export function SessionProvider({ me, children }: { me: Me; children: ReactNode }) {
  const [businessId, setBusinessId] = useState(() => {
    const saved = sessionStorage.getItem('businessId');
    return saved && me.businessIds?.includes(saved) ? saved : (me.businessIds?.[0] ?? '');
  });
  useEffect(() => {
    sessionStorage.setItem('businessId', businessId);
  }, [businessId]);
  return <SessionContext.Provider value={{ me, businessId, setBusinessId }}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const s = useContext(SessionContext);
  if (!s) throw new Error('useSession outside SessionProvider');
  return s;
}

export const useBusinessId = () => useSession().businessId;

/**
 * When a call returns 401 after sign-in (the session expired), drop cached data
 * and re-check /me, whose error sends the app to sign-in. Before sign-in, /me's
 * own 401 already does that; reacting to it too would refetch /me in a loop.
 */
export function useSignOutOn401() {
  const qc = useQueryClient();
  useEffect(() => {
    const onUnauth = () => {
      if (!qc.getQueryData(keys.me)) return;
      qc.removeQueries({ predicate: (q) => !['brand', 'session-config', 'me'].includes(q.queryKey[0] as string) });
      qc.resetQueries({ queryKey: keys.me });
    };
    window.addEventListener(UNAUTHORISED_EVENT, onUnauth);
    return () => window.removeEventListener(UNAUTHORISED_EVENT, onUnauth);
  }, [qc]);
}

/** Sign out; `next` (a same-site path) is where to land after signing in again. */
export async function signOut(next?: string) {
  await fetch('/session/logout', { method: 'POST', credentials: 'same-origin' });
  sessionStorage.clear();
  window.location.assign(next ? `/login?next=${encodeURIComponent(next)}` : '/login');
}
